import React, { useMemo } from 'react';
import StaticKeyMetricCard from './StaticKeyMetricCard';
import { useDerivedMetricByWeekIdV2, SplineChartV2DataPoint } from 'src/components/BeaconV2Components/SplineChartV2';
import { weekIdToTimestamp } from 'src/components/BeaconV2Components/SplineChartV2/splineChartV2Utils';

interface ConversionCardProps {
  variant: 'large' | 'small' | 'medium';
  indexName: string;
  fieldName: string;
  title: string;
  onClick?: () => void;
}

/**
 * Converts SplineChartV2DataPoint[] to [timestamp, value][] format
 * needed by StaticKeyMetricCard
 */
const convertToTimestampFormat = (data: SplineChartV2DataPoint[]): [number, number][] => {
  return data.map((point) => [
    weekIdToTimestamp(point.weekId) ?? 0,
    point.value ?? 0
  ]);
};

/**
 * Conversion Card for Beacon - uses the same data source as TrafficConversionPage
 */
const ConversionCard = ({ fieldName, indexName, variant, title, onClick }: ConversionCardProps) => {
  // Use V2 hook for proper YTD + Prior Year handling (same as TrafficConversionPage)
  const {
    primaryData,
    comparisonData,
    isLoading,
    formattedPrimaryTotal,
    percentChange,
    formattedPercentChange
  } = useDerivedMetricByWeekIdV2({
    indexName: 'sales',
    fieldName: 'purchaseRate'
  });

  // Convert SplineChartV2 format to timestamp format for StaticKeyMetricCard
  const primaryDataTimestamp = useMemo(
    () => convertToTimestampFormat(primaryData),
    [primaryData]
  );

  const comparisonDataTimestamp = useMemo(
    () => convertToTimestampFormat(comparisonData),
    [comparisonData]
  );

  return (
    <StaticKeyMetricCard
      variant={variant}
      indexName={indexName}
      fieldName={fieldName}
      title={title}
      onClick={onClick}
      isLoading={isLoading}
      primaryData={primaryDataTimestamp}
      secondaryData={comparisonDataTimestamp}
      formattedPrimaryTotal={formattedPrimaryTotal}
      percentChange={percentChange}
      formattedPercentChange={formattedPercentChange}
    />
  );
};

export default ConversionCard;
